import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import CampaignCta from "@/components/CampaignCta";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/hooks/use-toast";
import { trackLeadSubmit } from "@/lib/analytics";
import {
  RESOURCES_ENABLED,
  LOOPS_LEAD_FORM_URL,
  RESOURCE_ASSETS,
  type ResourceAsset,
} from "@/config/resources";

interface ResourceLandingPageProps {
  assetId: ResourceAsset["id"];
}

/**
 * Gated lead-magnet landing page. SCAFFOLDED — see src/config/resources.ts.
 * With RESOURCES_ENABLED false this renders a "coming soon" state and does not
 * collect leads. The form + submit handler below are the live path for when
 * the PDFs and Loops form are ready.
 */
const ResourceLandingPage = ({ assetId }: ResourceLandingPageProps) => {
  const asset = RESOURCE_ASSETS[assetId];
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    role: "",
  });

  const update = (key: keyof typeof form) => (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => setForm((prev) => ({ ...prev, [key]: e.target.value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.email.includes("@") || !form.name.trim()) {
      toast({ variant: "destructive", title: "Please enter your name and work email." });
      return;
    }
    if (!LOOPS_LEAD_FORM_URL) {
      toast({
        variant: "destructive",
        title: "This download is not available yet.",
        description: "Please check back soon.",
      });
      return;
    }

    setSubmitting(true);
    try {
      // Mirrors the existing Loops form pattern used on the landing page.
      const body = new URLSearchParams({
        email: form.email.trim(),
        name: form.name.trim(),
        company: form.company.trim(),
        role: form.role.trim(),
        asset: asset.id,
      });
      const res = await fetch(LOOPS_LEAD_FORM_URL, {
        method: "POST",
        body: body.toString(),
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
      });
      if (!res.ok) {
        const data = await res.json().catch(() => null);
        throw new Error(data?.message || res.statusText);
      }

      trackLeadSubmit(asset.id);

      // TODO(resources): Do NOT count lead-gen as a Google Ads sign-up
      // conversion unless explicitly approved. Default is OFF. If approved:
      //   import { trackSignup } from "@/lib/analytics";
      //   await trackSignup(form.email.trim());

      navigate(asset.thankYouPath);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Something went wrong";
      toast({ variant: "destructive", title: "Submission failed", description: message });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Navbar />
      <main className="flex-grow">
        <section className="py-16 md:py-24">
          <div className="container mx-auto px-6 max-w-2xl">
            <p className="text-sm font-semibold text-primary mb-3">Free resource</p>
            <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground">
              {asset.title}
            </h1>
            <p className="mt-4 text-lg text-muted-foreground leading-relaxed">
              {asset.summary}
            </p>

            {!RESOURCES_ENABLED ? (
              <div className="mt-10 rounded-lg border bg-muted/30 p-8 text-center">
                <h2 className="text-xl font-semibold text-foreground">
                  Coming soon
                </h2>
                <p className="mt-2 text-muted-foreground">
                  This guide is on the way. In the meantime, you can start
                  protecting your Jotform forms today.
                </p>
                <div className="mt-6">
                  <CampaignCta location={`resource-${asset.id}-comingsoon`}>
                    Start free 14-day trial
                  </CampaignCta>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="mt-10 space-y-4 max-w-md">
                <div className="space-y-2">
                  <Label htmlFor="res-name">Name</Label>
                  <Input id="res-name" value={form.name} onChange={update("name")} required />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="res-email">Work email</Label>
                  <Input
                    id="res-email"
                    type="email"
                    value={form.email}
                    onChange={update("email")}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="res-company">Company</Label>
                  <Input
                    id="res-company"
                    value={form.company}
                    onChange={update("company")}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="res-role">Role</Label>
                  <Input id="res-role" value={form.role} onChange={update("role")} />
                </div>
                <Button type="submit" size="lg" disabled={submitting}>
                  {submitting ? "Sending..." : "Get the guide"}
                </Button>
              </form>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default ResourceLandingPage;
